import { useMemo, useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";

// Reused across all PlexianBody instances — Vector3 alloc inside useFrame is hot.
const _tmpVec3 = new THREE.Vector3();
const SPEECH_TRIGGER_DIST = 8;       // world units; show default bubble when within this radius
const SPEECH_TYPE_MS_PER_CHAR = 35;  // typewriter speed
const INTERACTIVE_DURATION_MS = 10_000; // how long interactive text stays before reverting

// ── Core geometry primitives ──────────────────────────────────────────────────

// Push 4-vertex horizontal ring. Returns [FL, FR, BR, BL] vertex indices.
function ring(V, cx, y, cz, w, d) {
  const hw = w / 2, hd = d / 2, b = V.length / 3;
  V.push(cx - hw, y, cz + hd,  cx + hw, y, cz + hd,
         cx + hw, y, cz - hd,  cx - hw, y, cz - hd);
  return [b, b + 1, b + 2, b + 3];
}

// 4 outward-facing quads connecting ring a (below) to ring b (above).
// Both rings are [FL, FR, BR, BL].
function quads(I, a, b) {
  I.push(
    a[0],a[1],b[1],  a[0],b[1],b[0],   // front  +Z
    a[1],a[2],b[2],  a[1],b[2],b[1],   // right  +X
    a[2],a[3],b[3],  a[2],b[3],b[2],   // back   -Z
    a[3],a[0],b[0],  a[3],b[0],b[3],   // left   -X
  );
}

// End caps
function capBot(I, r) { I.push(r[0], r[2], r[1],  r[0], r[3], r[2]); }
function capTop(I, r) { I.push(r[0], r[1], r[2],  r[0], r[2], r[3]); }

// ── 8-vertex chamfered rings (vertical subdivision for torso / neck / head) ──
//
// Each ring corner is chamfered at 45°, giving 4 flat faces + 4 diagonal faces.
// With flat shading every face has a distinct normal — all 8 edge loops visible.
//
// Vertex order (CCW from above, starting front-left):
//   0 front-left  (-hw+c, +hd)   4 back-right   (+hw-c, -hd)
//   1 front-right (+hw-c, +hd)   5 back-left    (-hw+c, -hd)
//   2 right-front (+hw,  +hd-c)  6 left-back    (-hw,  -hd+c)
//   3 right-back  (+hw,  -hd+c)  7 left-front   (-hw,  +hd-c)
//
const CHAMFER = 0.28;

function ring8(V, cx, y, cz, w, d) {
  const hw = w / 2, hd = d / 2, c = Math.min(hw, hd) * CHAMFER;
  const b = V.length / 3;
  V.push(
    cx - hw + c, y, cz + hd,      // 0
    cx + hw - c, y, cz + hd,      // 1
    cx + hw,     y, cz + hd - c,  // 2
    cx + hw,     y, cz - hd + c,  // 3
    cx + hw - c, y, cz - hd,      // 4
    cx - hw + c, y, cz - hd,      // 5
    cx - hw,     y, cz - hd + c,  // 6
    cx - hw,     y, cz + hd - c,  // 7
  );
  return [b, b+1, b+2, b+3, b+4, b+5, b+6, b+7];
}

function quads8(I, a, b) {
  for (let i = 0; i < 8; i++) {
    const j = (i + 1) % 8;
    I.push(a[i], a[j], b[j],  a[i], b[j], b[i]);
  }
}

function capTop8(I, r) { for (let i = 1; i < 7; i++) I.push(r[0], r[i], r[i + 1]); }

// Adapter ring4 a (below) → ring8 b (above).
// Each face: one proper quad + one chamfer triangle at the near corner of a.
function quads4to8(I, a, b) {
  for (let i = 0; i < 4; i++) {
    const aj = (i + 1) % 4;
    const bi0 = i * 2, bi1 = i * 2 + 1, bi_ch = (i * 2 + 7) % 8;
    I.push(a[i], a[aj], b[bi1],  a[i], b[bi1], b[bi0]);  // main face quad
    I.push(a[i], b[bi0], b[bi_ch]);                       // chamfer at a[i] corner
  }
}

// Adapter ring8 a (below) → ring4 b (above).
// Each face: one proper quad + one chamfer triangle at the near corner of b.
function quads8to4(I, a, b) {
  for (let i = 0; i < 4; i++) {
    const bj = (i + 1) % 4;
    const ai0 = i * 2, ai1 = i * 2 + 1, ai_ch = (i * 2 + 7) % 8;
    I.push(a[ai0], a[ai1], b[bj],  a[ai0], b[bj], b[i]);  // main face quad
    I.push(b[i], a[ai_ch], a[ai0]);                        // chamfer at b[i] corner
  }
}

// 8-vertex split ring: [FL, FC_L, FC_R, FR, BL, BC_L, BC_R, BR]
// Returns subsets usable as 4-vertex rings for torso, arms, neck, and crotch.
function split8(V, y, outerW, innerW, d) {
  const hd = d / 2, hw = outerW / 2, iw = innerW / 2, b = V.length / 3;
  V.push(
    -hw, y, +hd,   -iw, y, +hd,    iw, y, +hd,    hw, y, +hd,
    -hw, y, -hd,   -iw, y, -hd,    iw, y, -hd,    hw, y, -hd,
  );
  return {
    outer: [b,     b + 3, b + 7, b + 4],  // [FL,  FR,  BR,  BL ]
    left:  [b,     b + 1, b + 5, b + 4],  // [FL,  FC_L,BC_L,BL ]
    right: [b + 2, b + 3, b + 7, b + 6],  // [FC_R,FR,  BR,  BC_R]
    inner: [b + 1, b + 2, b + 6, b + 5],  // [FC_L,FC_R,BC_R,BC_L]
  };
}

// ── Main builder ──────────────────────────────────────────────────────────────

function lerp(a, b, t) { return a + (b - a) * t; }

function buildHumanoidGeo(c) {
  const {
    legLength = 0.9,         legSpacing = 0.38,
    legWBot = 0.14,          legDBot = 0.12,
    legKneeFrac = 0.48,      legKneeCz = 0,
    legKneeBulge = 1.35,     legCalfBulge = 1.1,   legThighBulge = 1.2,

    torsoH = 1.25,
    torsoWBot = 0.9,         torsoDBot = 0.55,
    torsoWTop = 0.55,        torsoDTop = 0.40,
    torsoWaistPos = 0.32,    waistWMult = 0.68,     waistDMult = 0.68,
    torsoShoulderFlare = 1.0,

    neckLength = 0.22,       neckW = 0.16,          neckD = 0.13,

    headH = 0.56,
    headWBot = 0.52,         headDBot = 0.48,
    headWTop = 0.36,         headDTop = 0.32,
    headJawFrac = 0.22,      headJawMult = 1.0,

    armLength = 1.1,
    armWTop = 0.16,          armDTop = 0.13,
    armWBot = 0.07,          armDBot = 0.06,
    armAngle = 0,
  } = c;

  const V = [];
  const I = [];

  // ── Heights ───────────────────────────────────────────────────────────────
  const yFoot     = 0;
  const yAnkle    = legLength * 0.08;
  const yShin     = legLength * legKneeFrac * 0.58;
  const yKnee     = legLength * legKneeFrac;
  const yThigh    = legLength * lerp(legKneeFrac, 1.0, 0.6);
  const yHip      = legLength;

  const yLoTorso  = yHip + torsoH * torsoWaistPos * 0.47;
  const yWaist    = yHip + torsoH * torsoWaistPos;
  const yMidTorso = yHip + torsoH * lerp(torsoWaistPos, 1.0, 0.50);
  const yChest    = yHip + torsoH * lerp(torsoWaistPos, 1.0, 0.72);
  const yHiChest  = yHip + torsoH * lerp(torsoWaistPos, 1.0, 0.88);
  const yShoulder = yHip + torsoH;

  const yNeckBot  = yShoulder + 0.02;
  const yNeckMid  = yNeckBot + neckLength * 0.5;
  const yNeckTop  = yNeckBot + neckLength;
  const yHeadBot  = yNeckTop + 0.02;
  const yHeadJaw  = yHeadBot + headH * headJawFrac;
  const yHeadLow  = yHeadBot + headH * Math.max(headJawFrac + 0.12, 0.38);
  const yHeadMid  = yHeadBot + headH * Math.max(headJawFrac + 0.26, 0.54);
  const yHeadHigh = yHeadBot + headH * 0.76;
  const yHeadTop  = yHeadBot + headH;

  const lx = -legSpacing / 2;
  const rx =  legSpacing / 2;

  const hipInner  = torsoWBot * 0.12;
  const hipCX_L   = -(torsoWBot + hipInner) / 4;
  const hipCX_R   =  (torsoWBot + hipInner) / 4;
  const hipW_leg  = (torsoWBot - hipInner) / 2;
  const shoulderW = torsoWTop * torsoShoulderFlare;
  const kBW       = legWBot * legKneeBulge;
  const kBD       = legDBot * legKneeBulge * 0.88;

  // ── HIP CAPS ──────────────────────────────────────────────────────────────
  // Legs are split out as separate geometries (rigged limbs); seal the hip
  // sockets on the body so the pelvis is closed where the legs would attach.
  const hip = split8(V, yHip, torsoWBot, hipInner, torsoDBot);

  // Precompute leg ring sizes (still used by the standalone leg builder below)
  const wFt = legWBot,                                  dFt = legDBot;
  const wAk = legWBot * 1.04,                           dAk = legDBot * 1.02;
  const wSh = legWBot * legCalfBulge,                   dSh = legDBot * legCalfBulge * 0.9;
  const wKn = kBW,                                      dKn = kBD;
  const wTh = lerp(kBW, hipW_leg, 0.5) * legThighBulge, dTh = lerp(kBD, torsoDBot, 0.5);
  const wHl = hipW_leg,                                 dHl = torsoDBot;
  const cxTL = lerp(lx, hipCX_L, 0.5);
  const cxTR = lerp(rx, hipCX_R, 0.5);

  capBot(I, hip.inner);
  capBot(I, hip.left);
  capBot(I, hip.right);
  const legFaces = I.length;

  // ── TORSO ─────────────────────────────────────────────────────────────────
  const waistW = torsoWBot * waistWMult;
  const waistD = torsoDBot * waistDMult;

  // Precompute torso ring sizes at each level
  const wT0 = torsoWBot,                            dT0 = torsoDBot;
  const wT1 = lerp(torsoWBot, waistW, 0.47),        dT1 = lerp(torsoDBot, waistD, 0.47);
  const wT2 = waistW,                                dT2 = waistD;
  const wT3 = lerp(waistW, shoulderW, 0.50),        dT3 = lerp(waistD, torsoDTop, 0.50);
  const wT4 = lerp(waistW, shoulderW, 0.72) * 1.04, dT4 = lerp(waistD, torsoDTop, 0.72) * 1.02;
  const wT5 = lerp(waistW, shoulderW, 0.88) * 1.01, dT5 = lerp(waistD, torsoDTop, 0.88);
  const wT6 = shoulderW,                             dT6 = torsoDTop;

  const lo_torso  = ring8(V, 0, yLoTorso,  0, wT1, dT1);
  const waist     = ring8(V, 0, yWaist,    0, wT2, dT2);
  const mid_torso = ring8(V, 0, yMidTorso, 0, wT3, dT3);
  const chest     = ring8(V, 0, yChest,    0, wT4, dT4);
  const hi_chest  = ring8(V, 0, yHiChest,  0, wT5, dT5);
  const shoulder  = split8(V, yShoulder, shoulderW, neckW, torsoDTop);

  // Intermediate torso rings
  const t_lo_hip   = ring (V, 0, lerp(yHip,      yLoTorso,  0.5), 0, lerp(wT0,wT1,0.5), lerp(dT0,dT1,0.5));
  const t_lo_wst   = ring8(V, 0, lerp(yLoTorso,  yWaist,    0.5), 0, lerp(wT1,wT2,0.5), lerp(dT1,dT2,0.5));
  const t_up_wst   = ring8(V, 0, lerp(yWaist,    yMidTorso, 0.5), 0, lerp(wT2,wT3,0.5), lerp(dT2,dT3,0.5));
  const t_lo_cst   = ring8(V, 0, lerp(yMidTorso, yChest,    0.5), 0, lerp(wT3,wT4,0.5), lerp(dT3,dT4,0.5));
  const t_mid_cst  = ring8(V, 0, lerp(yChest,    yHiChest,  0.5), 0, lerp(wT4,wT5,0.5), lerp(dT4,dT5,0.5));
  const t_up_cst   = ring (V, 0, lerp(yHiChest,  yShoulder, 0.5), 0, lerp(wT5,wT6,0.5), lerp(dT5,dT6,0.5));

  quads(I, hip.outer, t_lo_hip);        quads4to8(I, t_lo_hip,  lo_torso);
  quads8(I, lo_torso,  t_lo_wst);      quads8(I, t_lo_wst,  waist);
  quads8(I, waist,     t_up_wst);      quads8(I, t_up_wst,  mid_torso);
  quads8(I, mid_torso, t_lo_cst);      quads8(I, t_lo_cst,  chest);
  quads8(I, chest,     t_mid_cst);     quads8(I, t_mid_cst, hi_chest);
  quads8to4(I, hi_chest, t_up_cst);     quads(I, t_up_cst, shoulder.outer);

  // ── SHOULDER CAPS ─────────────────────────────────────────────────────────
  // Arms are built as separate geometries (rigged limbs); seal the shoulder sockets
  // on the body so the torso is closed where the arms would attach.
  const A      = Math.PI * 0.72 + armAngle;
  const lArmCX = -(shoulderW + neckW) / 4;
  const rArmCX =  (shoulderW + neckW) / 4;
  capTop(I, shoulder.left);
  capTop(I, shoulder.right);

  const torsoArmFaces = I.length;

  // ── NECK + HEAD ────────────────────────────────────────────────────────────
  const wN0 = neckW,        dN0 = neckD;
  const wN1 = neckW * 0.94, dN1 = neckD * 0.94;
  const wN2 = neckW * 0.88, dN2 = neckD * 0.88;

  const neck_bot = ring (V, 0, yNeckBot,                     0, wN0,               dN0);
  const neck_lo  = ring8(V, 0, lerp(yNeckBot, yNeckMid, 0.5), 0, lerp(wN0,wN1,0.5), lerp(dN0,dN1,0.5));
  const neck_mid = ring8(V, 0, yNeckMid,                     0, wN1,               dN1);
  const neck_hi  = ring8(V, 0, lerp(yNeckMid, yNeckTop, 0.5), 0, lerp(wN1,wN2,0.5), lerp(dN1,dN2,0.5));
  const neck_top = ring8(V, 0, yNeckTop,                     0, wN2,               dN2);

  const jawW = headWBot * headJawMult;
  const jawD = headDBot * headJawMult * 0.94;

  // Precompute head ring sizes at each level
  const wH0 = headWBot,                          dH0 = headDBot;
  const wH1 = jawW,                              dH1 = jawD;
  const wH2 = lerp(jawW, headWTop * 1.12, 0.5), dH2 = lerp(jawD, headDTop * 1.08, 0.5);
  const wH3 = lerp(jawW, headWTop, 0.72) * 1.04, dH3 = lerp(jawD, headDTop, 0.72) * 0.95;
  const wH4 = headWTop * 1.08,                   dH4 = headDTop * 1.04;
  const wH5 = headWTop,                          dH5 = headDTop;

  const head_bot    = ring8(V, 0, yHeadBot,                      0, wH0,               dH0);
  const head_lo_jaw = ring8(V, 0, lerp(yHeadBot,  yHeadJaw,  0.5), 0, lerp(wH0,wH1,0.5), lerp(dH0,dH1,0.5));
  const head_jaw    = ring8(V, 0, yHeadJaw,                      0, wH1,               dH1);
  const head_md_jaw = ring8(V, 0, lerp(yHeadJaw,  yHeadLow,  0.5), 0, lerp(wH1,wH2,0.5), lerp(dH1,dH2,0.5));
  const head_low    = ring8(V, 0, yHeadLow,                      0, wH2,               dH2);
  const head_lo_mid = ring8(V, 0, lerp(yHeadLow,  yHeadMid,  0.5), 0, lerp(wH2,wH3,0.5), lerp(dH2,dH3,0.5));
  const head_mid    = ring8(V, 0, yHeadMid,                      0, wH3,               dH3);
  const head_md_hi  = ring8(V, 0, lerp(yHeadMid,  yHeadHigh, 0.5), 0, lerp(wH3,wH4,0.5), lerp(dH3,dH4,0.5));
  const head_high   = ring8(V, 0, yHeadHigh,                     0, wH4,               dH4);
  const head_lo_top = ring8(V, 0, lerp(yHeadHigh, yHeadTop,  0.5), 0, lerp(wH4,wH5,0.5), lerp(dH4,dH5,0.5));
  const head_top    = ring8(V, 0, yHeadTop,                      0, wH5,               dH5);

  quads(I, shoulder.inner, neck_bot);
  quads4to8(I, neck_bot,  neck_lo); quads8(I, neck_lo,    neck_mid);
  quads8(I, neck_mid,  neck_hi);    quads8(I, neck_hi,    neck_top);
  quads8(I, neck_top,  head_bot);
  quads8(I, head_bot,  head_lo_jaw); quads8(I, head_lo_jaw, head_jaw);
  quads8(I, head_jaw,  head_md_jaw); quads8(I, head_md_jaw, head_low);
  quads8(I, head_low,  head_lo_mid); quads8(I, head_lo_mid, head_mid);
  quads8(I, head_mid,  head_md_hi);  quads8(I, head_md_hi,  head_high);
  quads8(I, head_high, head_lo_top); quads8(I, head_lo_top, head_top);
  capTop8(I, head_top);

  const headFaces = I.length;

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(V), 3));
  geo.setIndex(new THREE.Uint16BufferAttribute(new Uint16Array(I), 1));
  geo.computeVertexNormals();

  geo.addGroup(0,              legFaces,                   0);
  geo.addGroup(legFaces,       torsoArmFaces - legFaces,   1);
  geo.addGroup(torsoArmFaces,  headFaces - torsoArmFaces,  2);

  // ── ARM GEOMETRIES (separate, local-origin at shoulder joint) ─────────────
  // Each arm is built in local coordinates so that the shoulder joint sits at
  // the local origin (0, 0, 0). Rotating the parent <group> rotates the arm
  // around the shoulder. At rest, ldx/ldy/rdx/rdy point the arm down-and-out
  // (mirroring the original merged geometry).
  const wA0 = armWBot,        dA0 = armDBot;
  const wA1 = armWBot * 1.3,  dA1 = armDBot * 1.3;
  const wA2 = armWBot * 1.55, dA2 = armDBot * 1.55;
  const wA3 = armWTop * 0.72, dA3 = armDTop * 0.72;
  const wA4 = armWTop * 0.92, dA4 = armDTop * 0.92;
  const wA5 = armWTop,        dA5 = armDTop;

  function buildArm(side) {
    const dx = side === 'left' ? -Math.sin(A) :  Math.sin(A);
    const dy = Math.cos(A);
    const aV = [], aI = [];
    const r = (dist, w, d) => ring(aV, dx * dist, dy * dist, 0, w, d);

    const hand     = r(armLength,        wA0,                dA0);
    const lo_wrist = r(armLength * 0.91, lerp(wA0,wA1,0.5),  lerp(dA0,dA1,0.5));
    const wrist    = r(armLength * 0.82, wA1,                dA1);
    const lo_fore  = r(armLength * 0.72, lerp(wA1,wA2,0.5),  lerp(dA1,dA2,0.5));
    const forearm  = r(armLength * 0.62, wA2,                dA2);
    const lo_elbow = r(armLength * 0.55, lerp(wA2,wA3,0.5),  lerp(dA2,dA3,0.5));
    const elbow    = r(armLength * 0.48, wA3,                dA3);
    const lo_bicep = r(armLength * 0.37, lerp(wA3,wA4,0.5),  lerp(dA3,dA4,0.5));
    const bicep    = r(armLength * 0.26, wA4,                dA4);
    const arm_top  = r(armLength * 0.13, lerp(wA4,wA5,0.5),  lerp(dA4,dA5,0.5));
    const shoulderRing = r(0,            wA5,                dA5);

    capBot(aI, hand);
    quads(aI, hand,    lo_wrist);  quads(aI, lo_wrist,  wrist);
    quads(aI, wrist,   lo_fore);   quads(aI, lo_fore,   forearm);
    quads(aI, forearm, lo_elbow);  quads(aI, lo_elbow,  elbow);
    quads(aI, elbow,   lo_bicep);  quads(aI, lo_bicep,  bicep);
    quads(aI, bicep,   arm_top);   quads(aI, arm_top,   shoulderRing);
    capTop(aI, shoulderRing);

    const armGeo = new THREE.BufferGeometry();
    armGeo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(aV), 3));
    armGeo.setIndex(new THREE.Uint16BufferAttribute(new Uint16Array(aI), 1));
    armGeo.computeVertexNormals();
    return armGeo;
  }

  const armLGeo = buildArm('left');
  const armRGeo = buildArm('right');

  // ── LEG GEOMETRIES (separate, local-origin at hip joint) ──────────────────
  // Built as a 10-ring tube from foot up to the hip socket. Local origin is at
  // (lx/rx, yHip, 0) so the leg pivots from the hip when its <group> rotates.
  // Rest pose has the leg hanging straight down (foot at local y = -yHip).
  function buildLeg(side) {
    const sx     = side === 'left' ? lx     : rx;
    const cxT    = side === 'left' ? cxTL   : cxTR;
    const hipCX  = side === 'left' ? hipCX_L : hipCX_R;
    const lV = [], lI = [];
    const r = (cx, y, cz, w, d) => ring(lV, cx - sx, y - yHip, cz, w, d);

    const foot     = r(sx,                              yFoot,                     0,                        wFt,               dFt);
    const lo_ank   = r(sx,                              lerp(yFoot,   yAnkle, 0.5), 0,                       lerp(wFt,wAk,0.5), lerp(dFt,dAk,0.5));
    const ankle    = r(sx,                              yAnkle,                    0,                        wAk,               dAk);
    const up_ank   = r(sx,                              lerp(yAnkle,  yShin,  0.5), 0,                       lerp(wAk,wSh,0.5), lerp(dAk,dSh,0.5));
    const shin     = r(sx,                              yShin,                     0,                        wSh,               dSh);
    const lo_kn    = r(sx,                              lerp(yShin,   yKnee,  0.5), lerp(0,legKneeCz,0.5),   lerp(wSh,wKn,0.5), lerp(dSh,dKn,0.5));
    const knee     = r(sx,                              yKnee,                     legKneeCz,                wKn,               dKn);
    const up_kn    = r(lerp(sx, cxT, 0.5),              lerp(yKnee,   yThigh, 0.5), lerp(legKneeCz,0,0.5),   lerp(wKn,wTh,0.5), lerp(dKn,dTh,0.5));
    const thigh    = r(cxT,                             yThigh,                    0,                        wTh,               dTh);
    const lo_hip   = r(lerp(cxT, hipCX, 0.5),           lerp(yThigh,  yHip,   0.5), 0,                       lerp(wTh,wHl,0.5), lerp(dTh,dHl,0.5));
    const top      = r(hipCX,                           yHip,                      0,                        hipW_leg,           torsoDBot);

    capBot(lI, foot);
    quads(lI, foot,    lo_ank); quads(lI, lo_ank,   ankle);
    quads(lI, ankle,   up_ank); quads(lI, up_ank,   shin);
    quads(lI, shin,    lo_kn);  quads(lI, lo_kn,    knee);
    quads(lI, knee,    up_kn);  quads(lI, up_kn,    thigh);
    quads(lI, thigh,   lo_hip); quads(lI, lo_hip,   top);
    capTop(lI, top);

    const legGeo = new THREE.BufferGeometry();
    legGeo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(lV), 3));
    legGeo.setIndex(new THREE.Uint16BufferAttribute(new Uint16Array(lI), 1));
    legGeo.computeVertexNormals();
    return legGeo;
  }

  const legLGeo = buildLeg('left');
  const legRGeo = buildLeg('right');

  return {
    geo,
    armLGeo,
    armRGeo,
    legLGeo,
    legRGeo,
    y_head_base: yHeadBot,
    y_head_top:  yHeadTop,
    y_shoulder:  yShoulder,
    y_hip:       yHip,
    lArmCX,
    rArmCX,
    lLegCX:      lx,
    rLegCX:      rx,
  };
}

// ── Materials ─────────────────────────────────────────────────────────────────

function useFlatMat(color) {
  return useMemo(
    () => new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.76, metalness: 0.10, flatShading: true }),
    [color],
  );
}

const visorMat = new THREE.MeshStandardMaterial({
  color: new THREE.Color('#060610'), roughness: 0.12, metalness: 0.88,
});

function FaceSlot({ style = 'wide', w, h, z }) {
  const zPos = z + 0.012;

  if (style === 'cyclops') {
    const eyeSize = h * 1.45;
    return (
      <mesh position={[0, 0, zPos]} material={visorMat}>
        <boxGeometry args={[eyeSize, eyeSize, 0.04]} />
      </mesh>
    );
  }

  if (style === 'dual') {
    const slitH  = h * 0.36;
    const gap    = h * 0.22;
    const offset = (slitH + gap) / 2;
    return (
      <>
        <mesh position={[0,  offset, zPos]} material={visorMat}>
          <boxGeometry args={[w, slitH, 0.04]} />
        </mesh>
        <mesh position={[0, -offset, zPos]} material={visorMat}>
          <boxGeometry args={[w, slitH, 0.04]} />
        </mesh>
      </>
    );
  }

  if (style === 'goggles') {
    const eyeW      = w * 0.38;
    const eyeH      = h * 1.15;
    const eyeOffset = w * 0.28;
    return (
      <>
        <mesh position={[-eyeOffset, 0, zPos]} material={visorMat}>
          <boxGeometry args={[eyeW, eyeH, 0.04]} />
        </mesh>
        <mesh position={[ eyeOffset, 0, zPos]} material={visorMat}>
          <boxGeometry args={[eyeW, eyeH, 0.04]} />
        </mesh>
      </>
    );
  }

  // 'wide' (default)
  return (
    <mesh position={[0, 0, zPos]} material={visorMat}>
      <boxGeometry args={[w, h, 0.04]} />
    </mesh>
  );
}

// ── Decorative geometry helpers ───────────────────────────────────────────────

function makeCrownBlade(c) {
  const {
    crownBladeLength = 0.80, crownBladeW = 0.32,
    crownBladeD = 0.10,      crownBladeTipFrac = 0.06,
  } = c;
  const hw = crownBladeW / 2, hd = crownBladeD / 2;
  const tw = Math.max(0.01, crownBladeW * crownBladeTipFrac) / 2;
  const td = Math.max(0.01, crownBladeD * crownBladeTipFrac) / 2;
  const mh = crownBladeLength * 0.5;
  const mw = lerp(hw, tw, 0.5), md = lerp(hd, td, 0.5);
  // 12 vertices: 0-3=base, 4-7=mid, 8-11=tip
  const pos = [
    -hw, 0,  +hd,   hw, 0,  +hd,   hw, 0,  -hd,   -hw, 0,  -hd,
    -mw, mh, +md,   mw, mh, +md,   mw, mh, -md,   -mw, mh, -md,
    -tw, crownBladeLength, +td,   tw, crownBladeLength, +td,
     tw, crownBladeLength, -td,  -tw, crownBladeLength, -td,
  ];
  const idx = [
    // base → mid sides
    0,1,5, 0,5,4,  1,2,6, 1,6,5,  2,3,7, 2,7,6,  3,0,4, 3,4,7,
    // mid → tip sides
    4,5,9, 4,9,8,  5,6,10, 5,10,9,  6,7,11, 6,11,10,  7,4,8, 7,8,11,
    // base cap, tip cap
    0,3,2, 0,2,1,  8,9,10, 8,10,11,
  ];
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

function makeCenterSpike(c) {
  const { centerSpikeLength = 0.6, centerSpikeW = 0.08 } = c;
  const hw = centerSpikeW / 2;
  const mh = centerSpikeLength * 0.5, mw = hw * 0.5;
  // 9 vertices: 0-3=base, 4-7=mid, 8=tip
  const pos = [
    -hw,0,-hw, hw,0,-hw, hw,0,hw, -hw,0,hw,
    -mw,mh,-mw, mw,mh,-mw, mw,mh,mw, -mw,mh,mw,
    0,centerSpikeLength,0,
  ];
  const idx = [
    // base → mid
    0,1,5, 0,5,4,  1,2,6, 1,6,5,  2,3,7, 2,7,6,  3,0,4, 3,4,7,
    // mid → tip
    4,5,8, 5,6,8, 6,7,8, 7,4,8,
    // base cap
    0,3,2, 0,2,1,
  ];
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

function makeDorsal(w, h) {
  const hw = w / 2;
  const mh = h * 0.5, mw = hw * 0.5, md = 0.018;
  // 9 vertices: 0-3=base, 4-7=mid, 8=tip
  const pos = [
    -hw,0,-0.035, hw,0,-0.035, hw,0,0.035, -hw,0,0.035,
    -mw,mh,-md,   mw,mh,-md,   mw,mh,md,   -mw,mh,md,
    0,h,0,
  ];
  const idx = [
    // base → mid
    0,1,5, 0,5,4,  1,2,6, 1,6,5,  2,3,7, 2,7,6,  3,0,4, 3,4,7,
    // mid → tip
    4,5,8, 5,6,8, 6,7,8, 7,4,8,
    // base cap
    0,3,2, 0,2,1,
  ];
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

// ── Animation ────────────────────────────────────────────────────────────────
//
// Rigid-segment rig: body root + two arm joints (shoulder pivots).
// Each clip sets transforms on the appropriate refs every frame. We re-zero
// the transforms each call so switching clips never leaves stale state.

function applyAnimation(refs, animation, t) {
  const root = refs.root.current;
  const armL = refs.armL.current;
  const armR = refs.armR.current;
  const legL = refs.legL.current;
  const legR = refs.legR.current;
  if (!root) return;

  // Reset every frame so clip switches are clean
  root.position.set(0, 0, 0);
  root.rotation.set(0, 0, 0);
  if (armL) armL.rotation.set(0, 0, 0);
  if (armR) armR.rotation.set(0, 0, 0);
  if (legL) legL.rotation.set(0, 0, 0);
  if (legR) legR.rotation.set(0, 0, 0);

  if (animation === 'wave') {
    // Right arm raised + waving; legs essentially still (just a soft idle drift)
    // so the wave reads cleanly without distracting limb chatter.
    root.position.y = 0.03 * Math.sin(t * 1.6);
    root.rotation.z = -0.05;  // slight lean toward the raised arm
    if (armR) {
      armR.rotation.z = 2.27 + 0.20 * Math.sin(t * 6);
      armR.rotation.x = 0.10 * Math.sin(t * 6);
    }
    if (armL) {
      armL.rotation.x = 0.05 * Math.sin(t * 1.6);
    }
    // Legs remain at rest (no rotation set — both refs already zeroed above).
  } else if (animation === 'dance') {
    // Bouncy whole-body + alternating step + arm swings
    const beat = t * 3.6;
    root.position.y = 0.10 * Math.abs(Math.sin(t * 2.6));
    root.rotation.y = 0.50 * Math.sin(t * 1.8);
    root.rotation.z = 0.14 * Math.sin(beat);
    if (armL) {
      armL.rotation.z = -1.0 + 0.7 * Math.sin(beat);
      armL.rotation.x = 0.4 * Math.sin(beat + 0.3);
    }
    if (armR) {
      armR.rotation.z = 1.0 - 0.7 * Math.sin(beat);
      armR.rotation.x = 0.4 * Math.sin(beat + 0.3);
    }
    // Alternating step: one leg forward while the other lifts back
    if (legL) {
      legL.rotation.x = 0.55 * Math.sin(beat);
      legL.rotation.z = 0.10 * Math.sin(beat + 1.2);
    }
    if (legR) {
      legR.rotation.x = -0.55 * Math.sin(beat);
      legR.rotation.z = -0.10 * Math.sin(beat + 1.2);
    }
  } else {
    // 'idle' (default) — gentle breathing bob, weight-shift sway, subtle limb drift
    root.position.y = 0.045 * Math.sin(t * 1.5);
    root.rotation.y = 0.09 * Math.sin(t * 0.8);
    root.rotation.z = 0.025 * Math.sin(t * 0.7);  // gentle weight shift
    if (armL) armL.rotation.x = 0.07 * Math.sin(t * 1.4 + 0.5);
    if (armR) armR.rotation.x = 0.07 * Math.sin(t * 1.4);
    if (legL) legL.rotation.x = 0.03 * Math.sin(t * 1.4 + Math.PI);
    if (legR) legR.rotation.x = 0.03 * Math.sin(t * 1.4);
  }
}

// ── Body component ────────────────────────────────────────────────────────────

function PlexianBody({ c, matTop, matMid, matBot, interactionTrigger = 0 }) {
  const {
    headH    = 0.56,
    headWBot = 0.52, headDBot = 0.48,
    headWTop = 0.36,
    headOffsetX = 0, headTilt = 0,
    crownBladeAngle    = 0.50,
    crownBladeBackTilt = 0.10,
    hasCenterSpike     = false,
    hasDorsal          = false,
    dorsalCount        = 2,
    dorsalH            = 0.72,
    dorsalW            = 0.36,
    legLength          = 0.9,
    torsoH             = 1.25,
    torsoDBot          = 0.55,
    animation          = 'idle',
    visorStyle         = 'wide',
  } = c;
  // Speech config (backwards compat: old avatars had a single `speechText` field
  // — treat it as defaultText so existing placements keep working).
  const defaultText        = c.defaultText ?? c.speechText ?? '';
  const interactiveText    = c.interactiveText ?? '';
  const interactionEnabled = !!c.interactionEnabled && interactiveText.length > 0;

  const bodyResult = useMemo(
    () => buildHumanoidGeo(c),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [c.legLength, c.legWBot, c.legDBot, c.legSpacing,
     c.torsoH, c.torsoWBot, c.torsoDBot, c.torsoWTop, c.torsoDTop,
     c.neckLength, c.neckW, c.neckD,
     c.headH, c.headWBot, c.headDBot, c.headWTop, c.headDTop,
     c.armLength, c.armWTop, c.armDTop, c.armWBot, c.armDBot, c.armAngle],
  );

  const crownBladeGeo = useMemo(
    () => makeCrownBlade(c),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [c.crownBladeLength, c.crownBladeW, c.crownBladeD, c.crownBladeTipFrac],
  );

  const centerSpikeGeo = useMemo(
    () => hasCenterSpike ? makeCenterSpike(c) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasCenterSpike, c.centerSpikeLength, c.centerSpikeW],
  );

  const dorsalGeos = useMemo(() => {
    if (!hasDorsal) return [];
    return Array.from({ length: dorsalCount }, () => makeDorsal(dorsalW, dorsalH));
  }, [hasDorsal, dorsalCount, dorsalW, dorsalH]);

  const {
    geo: bodyGeo,
    armLGeo,
    armRGeo,
    legLGeo,
    legRGeo,
    y_head_base,
    y_head_top,
    y_shoulder,
    y_hip,
    lArmCX,
    rArmCX,
    lLegCX,
    rLegCX,
  } = bodyResult;

  const rootRef = useRef();
  const armLRef = useRef();
  const armRRef = useRef();
  const legLRef = useRef();
  const legRRef = useRef();
  const refs = useMemo(
    () => ({ root: rootRef, armL: armLRef, armR: armRRef, legL: legLRef, legR: legRRef }),
    [],
  );

  // Speech bubble:
  //   - Default text: shows when camera comes within range; types out.
  //   - Click (handled externally by CenterRaycaster's "plexian-avatar-click" event,
  //     which bumps the `interactionTrigger` prop): bubble flips to interactive
  //     text for 10s, then reverts to default. Hover label "Talk to Plexian" is
  //     rendered by the standard OutlineEffect via the model's hoverText —
  //     not by this component.
  const [displayText, setDisplayText] = useState('');
  const speechStateRef = useRef({ mode: 'default', currentTarget: '', startMs: 0 });
  const revertTimeoutRef = useRef(null);

  // Reset state when the underlying texts change so editing in the creator
  // immediately reflects (no leftover typed-out fragments of old text).
  useEffect(() => {
    speechStateRef.current.mode = 'default';
    speechStateRef.current.currentTarget = '';
    setDisplayText('');
    if (revertTimeoutRef.current) {
      clearTimeout(revertTimeoutRef.current);
      revertTimeoutRef.current = null;
    }
  }, [defaultText, interactiveText, interactionEnabled]);

  // Cleanup any pending revert when the component unmounts.
  useEffect(() => () => {
    if (revertTimeoutRef.current) clearTimeout(revertTimeoutRef.current);
  }, []);

  // External click trigger (from CenterRaycaster). Each bump of the counter
  // restarts a 10s interactive window. interactionTrigger=0 is the initial
  // state and intentionally does nothing.
  useEffect(() => {
    if (!interactionTrigger || !interactionEnabled || !interactiveText) return;
    speechStateRef.current.mode = 'interactive';
    speechStateRef.current.currentTarget = ''; // force typewriter reset on next frame
    if (revertTimeoutRef.current) clearTimeout(revertTimeoutRef.current);
    revertTimeoutRef.current = setTimeout(() => {
      speechStateRef.current.mode = 'default';
      revertTimeoutRef.current = null;
    }, INTERACTIVE_DURATION_MS);
  }, [interactionTrigger, interactionEnabled, interactiveText]);

  useFrame(({ clock, camera }) => {
    applyAnimation(refs, animation, clock.getElapsedTime());
    if (!rootRef.current) return;

    const state = speechStateRef.current;
    const nowMs = clock.getElapsedTime() * 1000;

    rootRef.current.getWorldPosition(_tmpVec3);
    const dist = camera.position.distanceTo(_tmpVec3);
    const nearby = dist < SPEECH_TRIGGER_DIST;

    let target = '';
    if (state.mode === 'interactive' && interactiveText) {
      target = interactiveText;
    } else if (nearby && defaultText) {
      target = defaultText;
    }

    if (target !== state.currentTarget) {
      state.currentTarget = target;
      state.startMs = nowMs;
    }

    if (target) {
      const elapsed = nowMs - state.startMs;
      const numChars = Math.min(target.length, Math.floor(elapsed / SPEECH_TYPE_MS_PER_CHAR));
      const next = target.slice(0, numChars);
      setDisplayText((prev) => (prev === next ? prev : next));
    } else {
      setDisplayText((prev) => (prev === '' ? prev : ''));
    }
  });

  const VISOR_FRAC = 0.42;
  const visorZf = (headDBot * (1 - VISOR_FRAC)) / 2 * 0.95;
  const visorW  = (headWBot * (1 - VISOR_FRAC) + headWTop * VISOR_FRAC) * 0.68;
  const visorH  = headH * 0.20;

  const dorsalSpacing = torsoH / (dorsalCount + 1);
  const dorsalBaseY   = legLength > 0.05 ? legLength : 0;

  return (
    <group ref={rootRef}>

      {/* Body: legs + hip + torso + neck + head (arms split out for rigging) */}
      <mesh geometry={bodyGeo} material={[matBot, matMid, matTop]} />

      {/* Arms — separate meshes pivoting at the shoulder joint */}
      <group ref={armLRef} position={[lArmCX, y_shoulder, 0]}>
        <mesh geometry={armLGeo} material={matMid} />
      </group>
      <group ref={armRRef} position={[rArmCX, y_shoulder, 0]}>
        <mesh geometry={armRGeo} material={matMid} />
      </group>

      {/* Legs — separate meshes pivoting at the hip joint */}
      <group ref={legLRef} position={[lLegCX, y_hip, 0]}>
        <mesh geometry={legLGeo} material={matBot} />
      </group>
      <group ref={legRRef} position={[rLegCX, y_hip, 0]}>
        <mesh geometry={legRGeo} material={matBot} />
      </group>

      {/* Dorsal fins */}
      {hasDorsal && dorsalGeos.map((geo, i) => (
        <mesh key={i} material={matMid}
          position={[0, dorsalBaseY + dorsalSpacing * (i + 1), -torsoDBot * 0.82]}>
          <primitive object={geo} attach="geometry" />
        </mesh>
      ))}

      {/* Head decorations: visor + V-crown */}
      <group position={[headOffsetX, 0, 0]} rotation={[headTilt, 0, 0]}>

        <group position={[0, y_head_base + headH * VISOR_FRAC, 0]}>
          <FaceSlot style={visorStyle} w={visorW} h={visorH} z={visorZf} />
        </group>

        <group position={[0, y_head_top, 0]}>
          <group rotation={[crownBladeBackTilt, 0,  crownBladeAngle]}>
            <mesh material={matTop}>
              <primitive object={crownBladeGeo} attach="geometry" />
            </mesh>
          </group>
          <group rotation={[crownBladeBackTilt, 0, -crownBladeAngle]}>
            <mesh material={matTop}>
              <primitive object={crownBladeGeo} attach="geometry" />
            </mesh>
          </group>
          {hasCenterSpike && centerSpikeGeo && (
            <group rotation={[crownBladeBackTilt, 0, 0]}>
              <mesh material={matTop}>
                <primitive object={centerSpikeGeo} attach="geometry" />
              </mesh>
            </group>
          )}
        </group>

      </group>

      {/* Speech bubble — proximity-triggered, typewriter effect */}
      {displayText && (
        <Html
          position={[0, y_head_top + 0.45, 0]}
          center
          distanceFactor={6}
          style={{ pointerEvents: 'none' }}
        >
          <div
            style={{
              background: '#000',
              color: '#fff',
              padding: '8px 14px',
              fontFamily: '"PT Serif", Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              fontSize: '15px',
              lineHeight: 1.25,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              maxWidth: '240px',
              textAlign: 'center',
              border: '2px solid #fff',
              whiteSpace: 'pre-wrap',
              userSelect: 'none',
            }}
          >
            {displayText}
          </div>
        </Html>
      )}

    </group>
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

function PlexianAvatar({ config, interactionTrigger = 0 }) {
  const matTop = useFlatMat(config?.colors?.top    ?? '#8b5cf6');
  const matMid = useFlatMat(config?.colors?.mid    ?? '#06b6d4');
  const matBot = useFlatMat(config?.colors?.bottom ?? '#f59e0b');

  if (!config) return null;

  return (
    <group>
      <PlexianBody
        c={config}
        matTop={matTop}
        matMid={matMid}
        matBot={matBot}
        interactionTrigger={interactionTrigger}
      />
    </group>
  );
}

export default PlexianAvatar;
